<template>
    <ModeSwitcher
        ref="modeSwitcher"
        :main-mode-text="$t('create')"
        :create-mode-text="$t('product.info.create_extra_service')"
        :edit-mode-text="$t('product.info.edit_extra_service')"
        @reset="editableExtraService = null"
    >
        <template v-slot:btn-block>
            <div class="full-width d-flex align-center">
                <v-spacer />
                <FilterStatusToggle v-model="activeFilter" />
            </div>
        </template>
        <template v-slot:main-mode>
            <v-data-table
                :headers="useHeaders"
                :items="useFilteredExtraServices"
                :items-per-page="-1"
                :items-per-page-options="[-1]"
                :loading="isLoading"
                :items-length="useFilteredExtraServices.length"
                :custom-key-sort="{
                    name: (a, b) => localized(a).localeCompare(localized(b)),
                }"
                :sort-by="[{key: 'categoryNameSortable', order: 'asc'}]"
                must-sort
                density="compact"
                color="primary"
                class="elevation-1 table-server"
                data-cy="extra-services-table"
            >
                <template v-slot:item.name="{item}">
                    <div class="text-link" @click="switchToEditMode(item)">
                        {{ localized(item.name) }}
                    </div>
                </template>
                <template v-slot:item.extraServiceCategoryName="{item}">
                    {{
                        extraServiceCategories.find(category => category.type === item.extraServiceCategoryName)
                            ?.name || ''
                    }}
                </template>
                <template v-slot:item.unitPricingType="{item}">
                    {{ item.unitPricingType && getUnitPricingType(item.unitPricingType) }}
                </template>
                <template v-slot:item.calculationType="{item}">
                    {{ $t(`calculationType.${item.calculationType}`) }}
                </template>
                <template v-slot:item.categoryId="{item}">
                    {{ extraServiceCategories.find(({id}) => id === item.categoryId)?.label || '' }}
                </template>
                <template v-slot:item.active="{item}">
                    {{ item.active ? $t('active') : $t('inactive') }}
                </template>
            </v-data-table>
        </template>
        <template v-slot:additional-mode>
            <InfoExtraServicesForm
                :editable-extra-service="editableExtraService"
                :contract-id="contractId"
                :service-type="serviceType"
                :guests-categories="guestsCategories"
                :extra-service-categories="filteredExtraServiceCategories"
                :app-method="appMethod"
                @save="save()"
                @cancel="modeSwitcher.goToTable()"
            />
        </template>
    </ModeSwitcher>
</template>

<script setup lang="ts">
    import InfoExtraServicesForm from '~src/components/products/information/InfoExtraServicesForm.vue'
    import {contractServiceType} from '~/utils/types/types'
    import {usePaxes} from '~/composables/form-helpers'
    import {capitalizeFirstLetter, clone} from '~/utils/helpers'

    const props = defineProps<{
        contractId: number
        serviceType: string
    }>()

    const localized = useLocalized()
    const isLoading = ref<boolean>(false)
    const extraServices = ref<any[]>([])
    const modeSwitcher = ref(null)
    const editableExtraService = ref(null)
    const guestsCategories = ref<any[]>([])
    const extraServiceCategories = ref([])
    const activeFilter = ref<boolean>(true)

    const app = useNuxtApp()
    const {t, locale} = useI18n()

    const appMethod = ref(null)
    const methods = {
        ownHotel: 'accommodationExtraServices',
        ownCharter: 'charterExtraServices',
        ownExcursion: 'ownProductExtraServices',
        concert: 'ownProductExtraServices',
        ownTransfer: 'ownProductExtraServices',
        ownCarRent: 'carRentExtraServices',
        ownCruise: 'ownProductExtraServices',
        tour: 'ownProductExtraServices',
        ownExtraService: 'ownProductExtraServices',
        ownVisa: 'ownProductExtraServices',
    }

    const availableForLoading: string[] = [
        contractServiceType.ownHotel,
        contractServiceType.ownCharter,
        contractServiceType.concert,
        contractServiceType.ownCruise,
        contractServiceType.tour,
        contractServiceType.ownExtraService,
    ]

    if (props.serviceType === contractServiceType.ownHotel) {
        watch(useHotelInfo, async () => {
            await loadData()
            modeSwitcher.value.goToTable()
        })
    }

    onMounted(async () => {
        appMethod.value = methods[props.serviceType]
        await loadData()
        if (props.serviceType === contractServiceType.ownHotel) {
            await loadHotelExtraServicesDictionaryInfo()
        } else {
            await loadExtraServiceCategories()
        }

        if (availableForLoading.includes(props.serviceType)) {
            if (!supplierStore.paxes?.length) await supplierStore.loadPaxes()
            preparePaxes()
        }
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {contractId: props.contractId}
            const res = await app.$api[appMethod.value].get(rq)
            extraServices.value = res.extraServices
        } catch (e) {
            extraServices.value = []
        } finally {
            isLoading.value = false
        }
    }

    async function loadHotelExtraServicesDictionaryInfo(): Promise<void> {
        try {
            const {extraServiceTypes} = await app.$api.hotelExtraServicesDictionaryInfo.get()
            extraServiceCategories.value = extraServiceTypes.sort((a: {name: string}, b: {name: string}) =>
                a.name.localeCompare(b.name)
            )
        } catch (e) {
            extraServiceCategories.value = []
        }
    }

    async function loadExtraServiceCategories(): Promise<void> {
        try {
            const rq = {contractId: props.contractId, locale: locale.value}
            const {param} = await app.$api.ownProductExtraServiceCategories.get(rq)
            extraServiceCategories.value = param.sort((a: {label: string}, b: {label: string}) =>
                a.label.localeCompare(b.label)
            )
        } catch (e) {
            extraServiceCategories.value = []
        }
    }

    watch(usePaxes, async () => {
        if (availableForLoading.includes(props.serviceType)) {
            preparePaxes()
            modeSwitcher.value.goToTable()
        }
    })

    const preparePaxes = (): void => {
        guestsCategories.value = clone(usePaxes.value).map(({name, id}) => ({
            value: id,
            title: localized(name) || id,
            enabled: true,
        }))
    }

    const save = async (): Promise<void> => {
        await loadData()
        modeSwitcher.value.goToTable()
    }

    const useHeaders = computed(() => {
        const headersWithCategory = [
            {title: t('name'), value: 'name', minWidth: '120px', sortable: true},
            {
                title: t('category'),
                value: 'extraServiceCategoryName',
                minWidth: '120px',
                sortable: true,
                sort: (a: any, b: any) =>
                    localized(a.categoryNameSortable).localeCompare(localized(b.categoryNameSortable)),
            },
            {title: t('calculation_type'), value: 'calculationType', minWidth: '120px'},
            {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
        ]
        switch (props.serviceType) {
            case contractServiceType.ownHotel:
            case contractServiceType.ownExcursion:
            case contractServiceType.ownTransfer:
                return [
                    {title: t('name'), value: 'name', minWidth: '120px', sortable: true},
                    {
                        title: t('category'),
                        value: 'extraServiceCategoryName',
                        minWidth: '120px',
                        sortable: true,
                        sort: (a: any, b: any) =>
                            localized(a.categoryNameSortable).localeCompare(localized(b.categoryNameSortable)),
                    },
                    {title: t('calculation_type'), value: 'calculationType', minWidth: '120px', sortable: true},
                    {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
                ]
            case contractServiceType.ownCharter:
                return [
                    {title: t('name'), value: 'name', minWidth: '120px', sortable: true},
                    {
                        title: t('category'),
                        value: 'extraServiceCategoryName',
                        minWidth: '120px',
                        sortable: true,
                        sort: (a: any, b: any) =>
                            localized(a.categoryNameSortable).localeCompare(localized(b.categoryNameSortable)),
                    },
                    {title: t('calculation_type'), value: 'unitPricingType', minWidth: '120px'},
                    {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
                ]
            case contractServiceType.ownCarRent:
                return headersWithCategory
            case contractServiceType.ownCruise:
                return headersWithCategory
            case contractServiceType.tour:
                return headersWithCategory
            case contractServiceType.ownExtraService:
                return headersWithCategory
            case contractServiceType.ownVisa:
                return [
                    {title: t('name'), value: 'name', minWidth: '120px', sortable: true},
                    {
                        title: t('category'),
                        value: 'extraServiceCategoryName',
                        minWidth: '120px',
                        sortable: true,
                        sort: (a: any, b: any) =>
                            localized(a.categoryNameSortable).localeCompare(localized(b.categoryNameSortable)),
                    },
                    {title: t('calculation_type'), value: 'unitPricingType', minWidth: '120px'},
                    {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
                ]
            default:
                return [
                    {title: t('name'), value: 'name', minWidth: '120px', sortable: true},
                    {
                        title: t('category'),
                        value: 'extraServiceCategoryName',
                        minWidth: '120px',
                        sortable: true,
                        sort: (a: any, b: any) =>
                            localized(a.categoryNameSortable).localeCompare(localized(b.categoryNameSortable)),
                    },
                    {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
                ]
        }
    })

    const getUnitPricingType = (unitPricingType: string) => {
        switch (unitPricingType) {
            case 'PER_UNIT':
                return capitalizeFirstLetter(`${t('per')} ${t('product.info.service').toLowerCase()}`)
            case 'PER_PAX':
                return capitalizeFirstLetter(`${t('per')} ${t('product.info.tourist').toLowerCase()}`)
            case 'PER_BEDROOM':
                return capitalizeFirstLetter(`${t('per')} ${t('product.info.bedroom').toLowerCase()}`)
            case 'PER_SERVICE':
                return capitalizeFirstLetter(`${t('per')} ${t('product.info.service').toLowerCase()}`)
            case 'PER_TOURIST':
                return capitalizeFirstLetter(`${t('per')} ${t('product.info.tourist').toLowerCase()}`)
        }
    }

    const switchToEditMode = (extraService: any): void => {
        modeSwitcher.value.edit()
        editableExtraService.value = extraService
        if (guestsCategories.value) {
            const enabledForPaxes = editableExtraService.value.enabledForPaxes || []
            guestsCategories.value = guestsCategories.value.map(({value, title}) => ({
                value,
                title,
                enabled: enabledForPaxes.includes(value),
            }))
        }
    }

    // Filter out eco categories when creating a new extra service
    const filteredExtraServiceCategories = computed(() => {
        // If editing an existing service, show all categories including eco categories
        if (editableExtraService.value) {
            return extraServiceCategories.value
        }

        // When creating a new service, filter out eco categories
        return extraServiceCategories.value.filter(
            (category: any) => category.type !== 'ECO_AMENITIES' && category.type !== 'ENERGY_SAVING_SOLUTIONS'
        )
    })

    const useFilteredExtraServices = computed(() => {
        // First filter by active status
        const filtered = extraServices.value.filter(({active}) => activeFilter.value === active)

        // Then add a sortable category name field to each item
        return filtered.map(item => {
            let categoryName = ''

            // Safely find the category name
            if (extraServiceCategories.value && Array.isArray(extraServiceCategories.value)) {
                const category = extraServiceCategories.value.find(
                    (cat: any) => cat.type === item.extraServiceCategoryName || cat.id === item.categoryId
                )
                categoryName = category ? category.name || category.label || '' : ''
            }

            return {
                ...item,
                // Add a sortable field for the category name
                categoryNameSortable: categoryName,
            }
        })
    })
</script>
